// src/hooks/useNavigation.ts
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

// Rutas disponibles en el sistema
const availableRoutes = [
  "/dashboard",
  "/PendingPayment",
  "/AdminConfigPage",
  "/PackagesPage",
  "/PetsPage",
  "/PetsPage/CreatePetPage",
  "/UnitSelectionPage",
  "/RoleSelectionPage",
  "/ApartmentSelectionPage",
  "/ChangePasswordPage",
  "/UserProfilePage",
  "/encomiendas",
  "/pqr",
  "/desperfectos",
  "/visitantes",
  "/visitantes/crear",
  "/mascotas-perdidas",
  "/vehiculos",
  "/tablero",
  "/apartamentos",
  "/zonas-comunes",
  "/asambleas",
  "/reservas",
  "/configurar-sos",
  "/configurar-sistema",
  "/informacion-general"
];

// Función para normalizar rutas y manejar variaciones
const normalizePath = (path: string): string => {
  if (!path) return "";

  // Normalizar la ruta: eliminar espacios, convertir a minúsculas, eliminar barras al inicio/final duplicadas
  let normalized = path.trim().toLowerCase();

  // Asegurar que empiece con /
  if (!normalized.startsWith("/")) {
    normalized = "/" + normalized;
  }

  // Eliminar barras duplicadas
  normalized = normalized.replace(/\/+/g, "/");

  // Mapeo de variaciones comunes a rutas estándar
  const pathMappings: { [key: string]: string } = {
    "/reserva": "/reservas",
    "/reservas-de-zonas-comunes": "/reservas",
    "/reservas-zonas-comunes": "/reservas",
    "/reservaszonascomunes": "/reservas",
  };

  // Verificar si hay un mapeo directo
  if (pathMappings[normalized]) {
    return pathMappings[normalized];
  }

  // Verificar si contiene palabras clave relacionadas con reservas
  if (normalized.includes("reserva") && !normalized.includes("reservas")) {
    return "/reservas";
  }

  return normalized;
};

export const useNavigation = () => {
  const navigate = useNavigate();

  const handleNavigation = async (path: string, label: string) => {
    if (!path) {
      return false;
    }

    // Normalizar la ruta
    const normalizedPath = normalizePath(path);

    // Verificar si la ruta está disponible (comparación exacta o normalizada)
    const isAvailable = availableRoutes.includes(path) || availableRoutes.includes(normalizedPath);

    if (!isAvailable) {
      // Mostrar alerta de próximamente con SweetAlert2
      await Swal.fire({
        title: '🚧 Próximamente',
        html: `
          <div style="text-align: center;">
            <div style="font-size: 3rem; margin-bottom: 1rem;">🛠️</div>
            <p style="font-size: 1.1rem; color: #666; margin-bottom: 0.5rem;">
              La sección <strong>${label}</strong> está en desarrollo
            </p>
            <p style="font-size: 0.9rem; color: #888;">
              Pronto estará disponible para ti
            </p>
          </div>
        `,
        icon: 'info',
        confirmButtonText: 'Entendido',
        confirmButtonColor: '#3085d6',
        background: '#fefefe',
        backdrop: 'rgba(0,0,0,0.4)',
        showClass: {
          popup: 'animate__animated animate__fadeInDown animate__faster'
        },
        hideClass: {
          popup: 'animate__animated animate__fadeOutUp animate__faster'
        },
        customClass: {
          popup: 'rounded-xl shadow-2xl',
          title: 'text-2xl font-bold text-gray-800',
          confirmButton: 'px-6 py-2 rounded-lg font-semibold'
        }
      });
      return false; // Indica que no se navegó
    }

    // Usar la ruta normalizada si es diferente de la original y está disponible
    const finalPath = availableRoutes.includes(normalizedPath) ? normalizedPath : path;

    // Si la ruta está disponible, navegar normalmente
    navigate(finalPath);
    return true; // Indica que se navegó exitosamente
  };

  return { handleNavigation };
};